/**
 * PremiumBirthDateTimePicker
 * iOS-style wheel picker for birth date AND time selection
 * 
 * Features:
 * - 3 synchronized wheel pickers for Date (Day, Month, Year)
 * - 2 synchronized wheel pickers for Time (Hour, Minute)
 * - "Doğum saatimi bilmiyorum" checkbox with visual disabled state
 * - Automatic invalid date correction (Feb 29, 30/31 day months)
 * - Fast year navigation (doesn't require endless scrolling)
 * - Turkish month names
 * - Full accessibility support (keyboard, aria, screen reader)
 * - prefers-reduced-motion support
 * - Mobile-first UX with large touch targets
 * - API payload format preserved (YYYY-MM-DD and HH:MM)
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import PremiumWheelPicker from './PremiumWheelPicker';

// === CONSTANTS ===

const CURRENT_YEAR = new Date().getFullYear();
const START_YEAR = 1920;

// Turkish months
const MONTHS = [
  { value: 1, label: 'Ocak' },
  { value: 2, label: 'Şubat' },
  { value: 3, label: 'Mart' },
  { value: 4, label: 'Nisan' },
  { value: 5, label: 'Mayıs' },
  { value: 6, label: 'Haziran' },
  { value: 7, label: 'Temmuz' },
  { value: 8, label: 'Ağustos' },
  { value: 9, label: 'Eylül' },
  { value: 10, label: 'Ekim' },
  { value: 11, label: 'Kasım' },
  { value: 12, label: 'Aralık' },
];

// Hours 00-23
const HOURS = Array.from({ length: 24 }, (_, i) => ({
  value: i,
  label: String(i).padStart(2, '0')
}));

// Minutes 0-55 (5-minute intervals for cleaner UX, change to 60 for 1-min)
const MINUTES = Array.from({ length: 12 }, (_, i) => ({
  value: i * 5,
  label: String(i * 5).padStart(2, '0')
}));

// === HELPER FUNCTIONS ===

// Generate years list (current year first for fast selection)
const generateYears = () => {
  const years = [];
  for (let y = CURRENT_YEAR; y >= START_YEAR; y--) {
    years.push(y);
  }
  return years;
};

// Check if leap year
const isLeapYear = (year) => {
  return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
};

// Get days in month (handles leap year for February)
const getDaysInMonth = (year, month) => {
  if (!year || !month) return 31;
  
  const daysInMonths = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  let days = daysInMonths[month - 1];
  
  // February leap year check
  if (month === 2 && isLeapYear(year)) {
    days = 29;
  }
  
  return days;
};

// Generate days array for given month/year
const generateDays = (year, month) => {
  const daysInMonth = getDaysInMonth(year, month);
  return Array.from({ length: daysInMonth }, (_, i) => i + 1);
};

// Format date as YYYY-MM-DD
const formatDate = (day, month, year) => {
  if (!day || !month || !year) return '';
  const d = String(day).padStart(2, '0');
  const m = String(month).padStart(2, '0');
  return `${year}-${m}-${d}`;
};

// Format time as HH:MM
const formatTime = (hour, minute) => {
  if (hour === null || hour === undefined) return '';
  const h = String(hour).padStart(2, '0');
  const m = String(minute || 0).padStart(2, '0');
  return `${h}:${m}`;
};

// Parse date from YYYY-MM-DD
const parseDate = (dateStr) => {
  if (!dateStr) return { day: null, month: null, year: null };
  const parts = dateStr.split('-');
  if (parts.length !== 3) return { day: null, month: null, year: null };
  return {
    year: parseInt(parts[0]) || null,
    month: parseInt(parts[1]) || null,
    day: parseInt(parts[2]) || null
  };
};

// Parse time from HH:MM
const parseTime = (timeStr) => {
  if (!timeStr) return { hour: 12, minute: 0 }; // Default to noon
  const parts = timeStr.split(':');
  if (parts.length < 2) return { hour: 12, minute: 0 };
  return {
    hour: parseInt(parts[0]) || 0,
    minute: parseInt(parts[1]) || 0
  };
};

// === MAIN COMPONENT ===

const PremiumBirthDateTimePicker = ({ 
  // Date props
  dateValue,
  onDateChange,
  dateLabel = 'Doğum Tarihi',
  dateRequired = false,
  dateTestId = 'premium-birth-date',
  
  // Time props
  timeValue,
  onTimeChange,
  timeLabel = 'Doğum Saati',
  timeRequired = false,
  timeTestId = 'premium-birth-time',
  
  // Unknown time props
  unknownTime = false,
  onUnknownTimeChange,
  showUnknownTimeOption = true,
  
  // Combined mode props
  showTimePicker = true,
  compact = false // For inline use
}) => {
  const [isDateOpen, setIsDateOpen] = useState(false);
  const [isTimeOpen, setIsTimeOpen] = useState(false);
  
  // Date state
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  
  // Time state
  const [selectedHour, setSelectedHour] = useState(12);
  const [selectedMinute, setSelectedMinute] = useState(0);
  
  // Internal unknown time state (if not controlled)
  const [internalUnknownTime, setInternalUnknownTime] = useState(unknownTime);
  
  // Use controlled or internal unknown time state
  const isUnknownTime = onUnknownTimeChange !== undefined ? unknownTime : internalUnknownTime;
  const setUnknownTime = onUnknownTimeChange !== undefined 
    ? (val) => onUnknownTimeChange(val)
    : setInternalUnknownTime;
  
  const initializedRef = useRef(false);
  const years = useMemo(() => generateYears(), []);

  // Initialize from external values
  useEffect(() => {
    if (!initializedRef.current) {
      if (dateValue) {
        const parsed = parseDate(dateValue);
        if (parsed.year) {
          setSelectedYear(parsed.year);
          setSelectedMonth(parsed.month);
          setSelectedDay(parsed.day);
        }
      }
      
      if (timeValue && !isUnknownTime) {
        const parsed = parseTime(timeValue);
        // Round to nearest 5 minutes
        setSelectedHour(parsed.hour);
        setSelectedMinute(Math.round(parsed.minute / 5) * 5);
      }
      
      initializedRef.current = true;
    }
  }, [dateValue, timeValue, isUnknownTime]);

  // Generate days based on selected month/year with memoization
  const days = useMemo(() => {
    return generateDays(selectedYear || CURRENT_YEAR, selectedMonth || 1);
  }, [selectedYear, selectedMonth]);

  // === DATE HANDLERS ===

  // Handle month change - auto-correct invalid days
  const handleMonthChange = useCallback((month) => {
    setSelectedMonth(month);
    const maxDays = getDaysInMonth(selectedYear || CURRENT_YEAR, month);
    if (selectedDay && selectedDay > maxDays) {
      setSelectedDay(maxDays);
    }
  }, [selectedYear, selectedDay]);

  // Handle year change - auto-correct invalid days (Feb 29 on non-leap year)
  const handleYearChange = useCallback((year) => {
    setSelectedYear(year);
    const maxDays = getDaysInMonth(year, selectedMonth || 1);
    if (selectedDay && selectedDay > maxDays) {
      setSelectedDay(maxDays);
    }
  }, [selectedMonth, selectedDay]);

  // Confirm date selection
  const handleDateConfirm = useCallback(() => {
    if (selectedDay && selectedMonth && selectedYear) {
      const formattedDate = formatDate(selectedDay, selectedMonth, selectedYear);
      onDateChange(formattedDate);
      setIsDateOpen(false);
    }
  }, [selectedDay, selectedMonth, selectedYear, onDateChange]);

  // === TIME HANDLERS ===

  // Confirm time selection
  const handleTimeConfirm = useCallback(() => {
    if (!isUnknownTime) {
      const formattedTime = formatTime(selectedHour, selectedMinute);
      onTimeChange(formattedTime);
    }
    setIsTimeOpen(false);
  }, [selectedHour, selectedMinute, onTimeChange, isUnknownTime]);

  // Handle unknown time checkbox
  const handleUnknownTimeToggle = useCallback((e) => {
    const checked = e.target.checked;
    setUnknownTime(checked);
    
    if (checked) {
      // Clear time value when unknown is checked
      onTimeChange('');
    }
  }, [setUnknownTime, onTimeChange]);

  // === DISPLAY HELPERS ===

  // Get formatted date display text
  const getDateDisplayText = useCallback(() => {
    if (dateValue) {
      const parsed = parseDate(dateValue);
      if (parsed.year && parsed.month && parsed.day) {
        const monthName = MONTHS.find(m => m.value === parsed.month)?.label || '';
        return `${parsed.day} ${monthName} ${parsed.year}`;
      }
    }
    return '';
  }, [dateValue]);

  // Get formatted time display text
  const getTimeDisplayText = useCallback(() => {
    if (isUnknownTime) {
      return 'Bilinmiyor';
    }
    if (timeValue) {
      const parsed = parseTime(timeValue);
      return `${String(parsed.hour).padStart(2, '0')}:${String(parsed.minute).padStart(2, '0')}`;
    }
    return '';
  }, [timeValue, isUnknownTime]);

  // === STYLES ===

  const triggerButtonStyles = {
    width: '100%',
    padding: '16px',
    boxSizing: 'border-box',
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '12px',
    color: 'white',
    fontSize: '16px',
    textAlign: 'left',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    transition: 'border-color 0.2s, background 0.2s'
  };

  const triggerButtonHoverStyles = {
    ...triggerButtonStyles,
    borderColor: 'rgba(139,92,246,0.4)'
  };

  const modalOverlayStyles = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '16px'
  };

  const modalContentStyles = {
    background: 'linear-gradient(180deg, #1e1b4b 0%, #0f172a 100%)',
    borderRadius: '24px',
    width: '100%',
    maxWidth: '360px',
    padding: '20px',
    maxHeight: '80vh',
    display: 'flex',
    flexDirection: 'column'
  };

  const handleBarStyles = {
    display: 'none' // Hide for centered modal
  };

  const wheelContainerStyles = {
    display: 'flex',
    justifyContent: 'center',
    gap: '4px',
    marginBottom: '20px'
  };

  const previewBoxStyles = {
    background: 'rgba(139,92,246,0.12)',
    border: '1px solid rgba(139,92,246,0.25)',
    borderRadius: '12px',
    padding: '12px',
    textAlign: 'center',
    marginBottom: '16px'
  };

  const actionButtonsStyles = {
    display: 'flex',
    gap: '12px'
  };

  const cancelButtonStyles = {
    flex: 1,
    padding: '16px',
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: '12px',
    color: 'white',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer'
  };

  const confirmButtonStyles = (isEnabled) => ({
    flex: 1,
    padding: '16px',
    background: isEnabled
      ? 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)'
      : 'rgba(255,255,255,0.08)',
    border: 'none',
    borderRadius: '12px',
    color: 'white',
    fontSize: '16px',
    fontWeight: '600',
    cursor: isEnabled ? 'pointer' : 'not-allowed',
    opacity: isEnabled ? 1 : 0.5,
    boxShadow: isEnabled ? '0 4px 20px rgba(139,92,246,0.3)' : 'none'
  });

  // Animation variants
  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 }
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { 
      opacity: 1,
      scale: 1,
      transition: { type: 'spring', damping: 25, stiffness: 400 }
    },
    exit: { 
      opacity: 0,
      scale: 0.95,
      transition: { duration: 0.15 }
    }
  };

  return (
    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '12px' }}>
      
      {/* === DATE PICKER === */}
      <div>
        {dateLabel && (
          <label 
            style={{ 
              display: 'block', 
              color: 'rgba(255,255,255,0.6)', 
              fontSize: '13px', 
              marginBottom: '6px' 
            }}
            id={`${dateTestId}-label`}
          >
            {dateLabel} {dateRequired && <span style={{ color: '#ef4444' }}>*</span>}
          </label>
        )}
        
        <button
          type="button"
          onClick={() => setIsDateOpen(true)}
          data-testid={dateTestId}
          aria-labelledby={`${dateTestId}-label`}
          aria-haspopup="dialog"
          style={{
            ...triggerButtonStyles,
            color: getDateDisplayText() ? 'white' : 'rgba(255,255,255,0.4)'
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '18px' }}>📅</span>
            <span>{getDateDisplayText() || 'Tarih seçin...'}</span>
          </span>
          <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '12px' }}>▼</span>
        </button>
      </div>

      {/* === TIME PICKER === */}
      {showTimePicker && (
        <div>
          {timeLabel && (
            <label 
              style={{ 
                display: 'block', 
                color: 'rgba(255,255,255,0.6)', 
                fontSize: '13px', 
                marginBottom: '6px' 
            }}
              id={`${timeTestId}-label`}
            >
              {timeLabel} {timeRequired && !isUnknownTime && <span style={{ color: '#ef4444' }}>*</span>}
            </label>
          )}
          
          <button
            type="button"
            onClick={() => !isUnknownTime && setIsTimeOpen(true)}
            data-testid={timeTestId}
            aria-labelledby={`${timeTestId}-label`}
            aria-haspopup="dialog"
            disabled={isUnknownTime}
            style={{
              ...triggerButtonStyles,
              color: isUnknownTime 
                ? 'rgba(255,255,255,0.3)' 
                : getTimeDisplayText() ? 'white' : 'rgba(255,255,255,0.4)',
              opacity: isUnknownTime ? 0.6 : 1,
              cursor: isUnknownTime ? 'not-allowed' : 'pointer',
              background: isUnknownTime ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.06)'
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '18px' }}>🕐</span>
              <span>{getTimeDisplayText() || 'Saat seçin...'}</span>
            </span>
            {!isUnknownTime && (
              <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '12px' }}>▼</span>
            )}
          </button>

          {/* Unknown Time Checkbox */}
          {showUnknownTimeOption && (
            <div 
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
                marginTop: '10px',
                padding: '8px 0'
              }}
              data-testid={`${timeTestId}-unknown-checkbox`}
            >
              <input
                type="checkbox"
                id={`${timeTestId}-unknown-input`}
                checked={isUnknownTime}
                onChange={handleUnknownTimeToggle}
                style={{
                  width: '20px',
                  height: '20px',
                  accentColor: '#8b5cf6',
                  cursor: 'pointer',
                  marginTop: '2px',
                  flexShrink: 0
                }}
              />
              <label 
                htmlFor={`${timeTestId}-unknown-input`}
                style={{ 
                  color: 'rgba(255,255,255,0.7)', 
                  fontSize: '14px',
                  lineHeight: 1.4,
                  cursor: 'pointer'
                }}
              >
                Doğum saatimi bilmiyorum
              </label>
            </div>
          )}

          {/* Unknown Time Warning */}
          {isUnknownTime && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              style={{
                background: 'rgba(251,191,36,0.1)',
                border: '1px solid rgba(251,191,36,0.25)',
                borderRadius: '10px',
                padding: '12px',
                marginTop: '8px'
              }}
            >
              <div style={{ 
                display: 'flex', 
                alignItems: 'flex-start', 
                gap: '10px' 
              }}>
                <span style={{ fontSize: '16px', flexShrink: 0 }}>⚠️</span>
                <p style={{ 
                  color: 'rgba(251,191,36,0.9)', 
                  fontSize: '13px', 
                  margin: 0,
                  lineHeight: 1.5
                }}>
                  Doğum saati bilinmediği için bazı astrolojik hesaplamalar (Yükselen burç, Ev konumları) yaklaşık olabilir.
                </p>
              </div>
            </motion.div>
          )}
        </div>
      )}

      {/* === DATE MODAL === */}
      <AnimatePresence>
        {isDateOpen && (
          <motion.div
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            style={modalOverlayStyles}
            onClick={() => setIsDateOpen(false)}
            role="dialog"
            aria-modal="true"
            aria-label="Doğum tarihi seçin"
          >
            <motion.div
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              onClick={(e) => e.stopPropagation()}
              style={modalContentStyles}
            >
              {/* Handle bar */}
              <div style={handleBarStyles} />

              {/* Header */}
              <h3 style={{ 
                color: 'white', 
                textAlign: 'center', 
                marginBottom: '20px',
                fontSize: '18px',
                fontWeight: '600'
              }}>
                📅 Doğum Tarihi
              </h3>

              {/* Wheel Pickers */}
              <div style={wheelContainerStyles}>
                <PremiumWheelPicker
                  items={days}
                  value={selectedDay}
                  onChange={setSelectedDay}
                  label="Gün"
                  ariaLabel="Gün seçin"
                  width={65}
                  testId="date-picker-day"
                />
                <PremiumWheelPicker
                  items={MONTHS}
                  value={selectedMonth}
                  onChange={handleMonthChange}
                  label="Ay"
                  ariaLabel="Ay seçin"
                  width={95}
                  testId="date-picker-month"
                />
                <PremiumWheelPicker
                  items={years}
                  value={selectedYear}
                  onChange={handleYearChange}
                  label="Yıl"
                  ariaLabel="Yıl seçin"
                  width={75}
                  testId="date-picker-year"
                />
              </div>

              {/* Preview */}
              {selectedDay && selectedMonth && selectedYear && (
                <div style={previewBoxStyles}>
                  <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px', marginBottom: '4px' }}>
                    Seçilen Tarih
                  </div>
                  <div style={{ color: 'white', fontSize: '20px', fontWeight: '600' }}>
                    {selectedDay} {MONTHS.find(m => m.value === selectedMonth)?.label} {selectedYear}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div style={actionButtonsStyles}>
                <button
                  type="button"
                  onClick={() => setIsDateOpen(false)}
                  style={cancelButtonStyles}
                >
                  İptal
                </button>
                <button
                  type="button"
                  onClick={handleDateConfirm}
                  disabled={!selectedDay || !selectedMonth || !selectedYear}
                  style={confirmButtonStyles(selectedDay && selectedMonth && selectedYear)}
                >
                  Onayla ✓
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* === TIME MODAL === */}
      <AnimatePresence>
        {isTimeOpen && (
          <motion.div
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            style={modalOverlayStyles}
            onClick={() => setIsTimeOpen(false)}
            role="dialog"
            aria-modal="true"
            aria-label="Doğum saati seçin"
          >
            <motion.div
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              onClick={(e) => e.stopPropagation()}
              style={modalContentStyles}
            >
              {/* Handle bar */}
              <div style={handleBarStyles} />

              {/* Header */}
              <h3 style={{ 
                color: 'white', 
                textAlign: 'center', 
                marginBottom: '20px',
                fontSize: '18px',
                fontWeight: '600'
              }}>
                🕐 Doğum Saati
              </h3>

              {/* Wheel Pickers */}
              <div style={{ ...wheelContainerStyles, gap: '16px' }}>
                <PremiumWheelPicker
                  items={HOURS}
                  value={selectedHour}
                  onChange={setSelectedHour}
                  label="Saat"
                  ariaLabel="Saat seçin"
                  width={80}
                  testId="time-picker-hour"
                />
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  color: 'rgba(255,255,255,0.3)',
                  fontSize: '32px',
                  fontWeight: '300',
                  paddingTop: '24px'
                }}>
                  :
                </div>
                <PremiumWheelPicker
                  items={MINUTES}
                  value={selectedMinute}
                  onChange={setSelectedMinute}
                  label="Dakika"
                  ariaLabel="Dakika seçin"
                  width={80}
                  testId="time-picker-minute"
                />
              </div>

              {/* Preview */}
              <div style={previewBoxStyles}>
                <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px', marginBottom: '4px' }}>
                  Seçilen Saat
                </div>
                <div style={{ color: 'white', fontSize: '28px', fontWeight: '600', fontFamily: 'monospace' }}>
                  {String(selectedHour).padStart(2, '0')}:{String(selectedMinute).padStart(2, '0')}
                </div>
              </div>

              {/* Actions */}
              <div style={actionButtonsStyles}>
                <button
                  type="button"
                  onClick={() => setIsTimeOpen(false)}
                  style={cancelButtonStyles}
                >
                  İptal
                </button>
                <button
                  type="button"
                  onClick={handleTimeConfirm}
                  style={confirmButtonStyles(true)}
                >
                  Onayla ✓
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PremiumBirthDateTimePicker;
